import { LockOutlined } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Container,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError, getErrorMessage } from '@pro4-erp/core-web';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../api/requestPasswordReset';
import { RequestPasswordResetParams } from '../types';

const Page = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const form = useForm<RequestPasswordResetParams>({
    defaultValues: {
      email: '',
    },
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: requestPasswordReset,
    onSuccess: (data) => {
      // redirect user back to login screen
      navigate('/auth/login');

      // show toast
      enqueueSnackbar(
        t('The password reset email was sent successfully. Please check your email.', {
          defaultValue: 'The password reset email was sent successfully. Please check your email.',
        }),
        { variant: 'success', autoHideDuration: 10000 }
      );
    },
    onError: (error: ApiError) => {
      form.setError('root', { message: getErrorMessage(error.errorResponseDto.message) });
    },
  });

  const onSubmit = (data: RequestPasswordResetParams) => {
    form.clearErrors();

    requestPasswordResetMutation.mutate({
      email: data.email,
    });
  };

  return (
    <>
      {/* form helper text */}
      <FormHelperText>
        {t(
          'Please enter your valid email, we will send a password reset link to that email. The link will be valid for the next 8 hours.',
          {
            defaultValue:
              'Please enter your valid email, we will send a password reset link to that email. The link will be valid for the next 8 hours.',
          }
        )}
      </FormHelperText>
      {/* form */}
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        sx={{ width: '100%' }}
      >
        {/* email field */}
        <Controller
          control={form.control}
          name="email"
          rules={{
            required: t('Email is required.', {
              defaultValue: 'Email is required.',
            }).toString(),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
              message: t('Invalid email address.', {
                defaultValue: 'Invalid email address.',
              }).toString(),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mt: '1rem' }}
              label={t('Email', { defaultValue: 'Email' })}
              type="email"
              fullWidth
              error={!!form.formState.errors.email}
              helperText={form.formState.errors.email?.message?.toString()}
            />
          )}
        />
        {/* form validation error output */}
        {!!form.formState.errors.root && (
          <FormHelperText error>{form.formState.errors.root.message?.toString()}</FormHelperText>
        )}
        {/* submit button */}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: '1rem' }}
          disabled={requestPasswordResetMutation.isLoading}
          fullWidth
        >
          {t('Submit', { defaultValue: 'Submit' })}
        </Button>
      </Box>
    </>
  );
};

export default Page;
